/**
 * MacroPreview Component
 * Live preview of macro outputs with and without data
 */

import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Eye, Check, X, Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react'
import { Macro, MacroTestData } from '../../types/macros'
import { previewMacroWithData, previewMacroWithoutData, generateMacroSyntax } from '../../utils/macroHelpers'
import EmptyStateIllustration from '../ui/EmptyStateIllustration'

interface MacroPreviewProps {
  macros: Macro[]
  testData: MacroTestData
  onUpdateTestData: (key: string, value: string) => void
  onRemoveTestDataKey: (key: string) => void
}

const MacroPreview: React.FC<MacroPreviewProps> = ({
  macros,
  testData,
  onUpdateTestData,
  onRemoveTestDataKey
}) => {
  const [newKey, setNewKey] = useState('')
  const [newValue, setNewValue] = useState('')
  const [isTestDataExpanded, setIsTestDataExpanded] = useState(true)

  const handleAddTestData = () => {
    if (newKey.trim() && newValue.trim()) {
      onUpdateTestData(newKey.trim(), newValue.trim())
      setNewKey('')
      setNewValue('')
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddTestData()
    }
  }

  const uniqueMacroKeys = Array.from(new Set(macros.map(m => m.key)))

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <h3 className="text-xl font-bold text-[#1A2B49]">Live Preview</h3>
          <Eye className="w-5 h-5 text-[#FE4215]" />
        </div>
      </div>

      {/* Test Data Configuration */}
      <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => setIsTestDataExpanded(!isTestDataExpanded)}
          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center space-x-2">
            <span className="text-sm font-semibold text-[#1A2B49]">Test Data</span>
            <span className="text-xs text-[#6E6D7A]">
              ({Object.keys(testData).length} value{Object.keys(testData).length !== 1 ? 's' : ''})
            </span>
          </div>
          {isTestDataExpanded ? (
            <ChevronDown className="w-4 h-4 text-[#6E6D7A]" />
          ) : (
            <ChevronRight className="w-4 h-4 text-[#6E6D7A]" />
          )}
        </button>

        <AnimatePresence>
          {isTestDataExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="border-t border-gray-200"
            >
              <div className="p-4 space-y-3">
                {/* Current Test Data */}
                {Object.keys(testData).length > 0 && (
                  <div className="space-y-2 mb-4">
                    {Object.entries(testData).map(([key, value]) => (
                      <div
                        key={key}
                        className="flex items-center space-x-2 bg-white border border-gray-200 rounded-md p-2"
                      >
                        <code className="text-xs font-mono text-[#233D71] flex-shrink-0 min-w-[100px]">
                          {key}:
                        </code>
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => onUpdateTestData(key, e.target.value)}
                          className="flex-1 text-sm px-2 py-1 border-0 focus:ring-1 focus:ring-[#FE4215] rounded"
                        />
                        <button
                          onClick={() => onRemoveTestDataKey(key)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Remove"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add New Test Data */}
                <div className="space-y-2 pt-2 border-t border-gray-200">
                  <p className="text-xs text-[#6E6D7A] font-medium">Add Test Value</p>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={newKey}
                      onChange={(e) => setNewKey(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Key (e.g., firstname)"
                      className="flex-1 text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FE4215] focus:border-transparent"
                    />
                    <input
                      type="text"
                      value={newValue}
                      onChange={(e) => setNewValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Value (e.g., Alex)"
                      className="flex-1 text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FE4215] focus:border-transparent"
                    />
                    <button
                      onClick={handleAddTestData}
                      disabled={!newKey.trim() || !newValue.trim()}
                      className={`px-3 py-2 rounded-md transition-colors ${
                        newKey.trim() && newValue.trim()
                          ? 'bg-[#FE4215] text-white hover:bg-[#E5350A]'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                      title="Add test value"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Suggested Keys */}
                {uniqueMacroKeys.length > 0 && (
                  <div className="pt-2">
                    <p className="text-xs text-[#6E6D7A] mb-2">Macro keys in use:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {uniqueMacroKeys.map(key => (
                        <button
                          key={key}
                          onClick={() => setNewKey(key)}
                          className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                            testData[key]
                              ? 'bg-green-50 border-green-200 text-green-700'
                              : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                          }`}
                          title={testData[key] ? `Has value: ${testData[key]}` : 'Click to add value'}
                        >
                          {key}
                          {testData[key] && <Check className="inline-block w-3 h-3 ml-1" />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Preview Section */}
      {macros.length === 0 ? (
        <div className="py-8">
          <EmptyStateIllustration
            type="response"
            title="No Macros to Preview"
            description="Add macros from the builder to see live previews"
          />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-sm font-medium text-[#1A2B49]">With Data</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
              <span className="text-sm font-medium text-[#1A2B49]">Without Data</span>
            </div>
          </div>

          <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
            {macros.map((macro, index) => {
              const withData = previewMacroWithData(macro, testData)
              const withoutData = previewMacroWithoutData(macro)
              const hasData = testData[macro.key] !== undefined

              return (
                <motion.div
                  key={macro.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  {/* Macro Header */}
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-semibold text-gray-500">#{index + 1}</span>
                        <code className="text-xs font-mono text-[#233D71] bg-white px-2 py-1 rounded border border-gray-200">
                          {macro.key}
                        </code>
                      </div>
                      {hasData ? (
                        <span className="flex items-center space-x-1 text-xs text-green-600">
                          <Check className="w-3 h-3" />
                          <span>Has test data</span>
                        </span>
                      ) : (
                        <span className="flex items-center space-x-1 text-xs text-gray-500">
                          <X className="w-3 h-3" />
                          <span>No test data</span>
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Preview Grid */}
                  <div className="grid grid-cols-2 divide-x divide-gray-200">
                    {/* With Data */}
                    <div className="p-4">
                      <div className="mb-2">
                        <span className="text-xs font-medium text-[#6E6D7A]">Syntax:</span>
                        <code className="block mt-1 text-xs bg-gray-50 p-2 rounded font-mono text-gray-700 break-all">
                          {generateMacroSyntax(macro)}
                        </code>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-[#6E6D7A]">Output:</span>
                        <div
                          className={`mt-1 p-3 rounded min-h-[40px] ${
                            withData
                              ? 'bg-green-50 border border-green-200'
                              : 'bg-gray-50 border border-gray-200'
                          }`}
                        >
                          {withData ? (
                            <span className="text-sm text-[#1A2B49]">{withData}</span>
                          ) : (
                            <span className="text-sm text-gray-400 italic">Empty</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Without Data */}
                    <div className="p-4 bg-gray-50/50">
                      <div className="mb-2">
                        <span className="text-xs font-medium text-[#6E6D7A]">Syntax:</span>
                        <code className="block mt-1 text-xs bg-white p-2 rounded font-mono text-gray-700 break-all border border-gray-200">
                          {generateMacroSyntax(macro)}
                        </code>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-[#6E6D7A]">Output:</span>
                        <div className="mt-1 p-3 bg-white border border-gray-200 rounded min-h-[40px]">
                          {withoutData ? (
                            <span className="text-sm text-[#1A2B49]">{withoutData}</span>
                          ) : (
                            <span className="text-sm text-gray-400 italic">Empty</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default MacroPreview
